/* ==========================================================================
   AVRA - Send a Hint

   The carousel is the theme's own (data-carousel), so nothing here touches it.
   This file runs the note window: the live preview, and the send.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     CONFIG - the one block to edit.

     LIVE ROUTE: Klaviyo (set 2026-09-22). Every hint fires a "Hint Sent"
     event with the RECEIVER as the profile, so a Klaviyo flow triggered by
     that metric is what actually delivers the card.

     Two things in Klaviyo decide whether it arrives:
       1. A flow triggered by the "Hint Sent" metric must exist. The metric
          only appears in Klaviyo's list AFTER the first event fires, so send
          one test hint before going looking for it.
       2. That flow's email must be marked TRANSACTIONAL. A hint receiver
          never subscribed to anything, and Klaviyo will not send marketing
          email to a profile that has not consented - it would build the
          message and deliver nothing, silently.

     If the request fails for any reason the form falls back to the shopper's
     own mail app, so a hint is never simply lost. That fallback carries her
     words and the link, but NOT the designed card: a mailto: link can only
     carry plain text. No setting changes that; it is how mailto: works.

     NOTE: none of this applies to the TEXT channel, which never sends from
     AVRA at all. See the header of snippets/avra-hint-composer.liquid.

     To switch routes, clear companyId and fill in ONE of the others. First
     one filled wins.

     1. KLAVIYO  - set companyId to your Klaviyo public API key (Settings ->
        API keys -> Public API key / Site ID; six characters; public by
        design, safe in theme code). This fires a "Hint Sent" event with the
        receiver as the profile. In Klaviyo, build a flow triggered by that
        metric and design the email there. AVRA-Send-a-Hint-email.html has the
        card already built to paste in, with the {{ }} names this sends.

     2. EMAILJS  - the quickest route if you do not want a flow. Sign up, add
        an email service, paste AVRA-Send-a-Hint-email.html as the template
        body, then fill in the three ids.

     3. WEBHOOK  - any URL that accepts a JSON POST: Zapier, Make, a Shopify
        Function, your own endpoint. The payload is documented at buildPayload
        below.
     ---------------------------------------------------------------------- */

  var CONFIG = {
    klaviyo: {
      companyId: 'VxvGmN',        // AVRA public API key / Site ID
      metric: 'Hint Sent',
      revision: '2024-10-15'
    },
    emailjs: {
      serviceId: '',              // e.g. 'service_xxxxxxx'
      templateId: '',             // e.g. 'template_xxxxxxx'
      publicKey: ''               // e.g. 'user_xxxxxxxxxxxx'
    },
    webhook: {
      url: ''                     // e.g. 'https://hooks.zapier.com/...'
    }
  };

  function mode() {
    if (CONFIG.klaviyo.companyId) return 'klaviyo';
    if (CONFIG.emailjs.serviceId && CONFIG.emailjs.templateId && CONFIG.emailjs.publicKey) return 'emailjs';
    if (CONFIG.webhook.url) return 'webhook';
    return 'mailto';
  }

  /* The default opener when she writes no note of her own. Kept here rather
     than inline so the words are in one place. */
  var DEFAULT_SMS_OPENER = 'I’ve been dreaming about this one… hint hint.';

  var modal = null;
  var piece = {};
  var lastFocus = null;
  var sending = false;

  /* 'email' = AVRA sends it. 'text' = she sends it from her own phone.
     See the header comment in snippets/avra-hint-composer.liquid for why the
     text channel must never be wired to a sending service. */
  var channel = 'email';

  /* ------------------------------------------------------------------------
     Helpers
     ---------------------------------------------------------------------- */

  function val(name) {
    var el = modal && modal.querySelector('[name="' + name + '"]');
    return el && el.value ? el.value.trim() : '';
  }

  /* Shopify's image_url gives a protocol-relative //cdn.shopify.com/... URL.
     In a browser that resolves fine. In an email client it resolves to
     nothing and the picture is a broken box, so it is made absolute here. */
  function absolute(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) return 'https:' + url;
    return url;
  }

  function setStatus(message, isError) {
    var el = modal && modal.querySelector('[data-hint-status]');
    if (!el) return;
    el.textContent = message || '';
    el.classList.toggle('is-error', !!isError);
  }

  /* ------------------------------------------------------------------------
     Channel
     ---------------------------------------------------------------------- */

  function setChannel(next) {
    if (!modal) return;
    channel = next === 'text' ? 'text' : 'email';

    Array.prototype.forEach.call(modal.querySelectorAll('[data-hint-mode]'), function (btn) {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-hint-mode') === channel ? 'true' : 'false');
    });

    /* Fields carrying data-hint-only belong to one channel. Hiding them also
       takes them out of validate(), which skips anything hidden. */
    Array.prototype.forEach.call(modal.querySelectorAll('[data-hint-only]'), function (el) {
      var belongs = el.getAttribute('data-hint-only');
      el.hidden = belongs !== channel;
      if (el.hidden) el.classList.remove('has-error');
    });

    var card = modal.querySelector('[data-hint-card]');
    var sms = modal.querySelector('[data-hint-sms]');
    if (card) card.hidden = channel !== 'email';
    if (sms) sms.hidden = channel !== 'text';

    var button = modal.querySelector('[data-hint-submit]');
    if (button) button.textContent = channel === 'text' ? 'Open my messages' : 'Send to';

    setStatus('');
    paint();
  }

  /* The message her Messages app opens with. Her own note replaces the default
     opener when she writes one, so the text sounds like her and not like us. */
  function smsBody() {
    var greeting = val('hint_to_name') ? val('hint_to_name') + ' — ' : '';
    var opener = val('hint_note') || DEFAULT_SMS_OPENER;
    var piecePart = piece.title ? ' ' + piece.title + ' from AVRA:' : ' From AVRA:';
    return greeting + opener + piecePart + ' ' + (piece.url || '');
  }

  /* Android reads ?body=, iOS reads &body=. Carrying both keeps one href
     working on either phone. */
  function smsHref() {
    var digits = val('hint_to_phone').replace(/[^\d+]/g, '');
    var body = encodeURIComponent(smsBody());
    return 'sms:' + digits + '?body=' + body + '&body=' + body;
  }

  /* ------------------------------------------------------------------------
     Live preview - the right pane is the card that gets emailed, so it is
     filled from the same values the send uses.
     ---------------------------------------------------------------------- */

  function paint() {
    if (!modal) return;

    var from = val('hint_from');
    var to = val('hint_to_name');
    var note = val('hint_note');

    Array.prototype.forEach.call(modal.querySelectorAll('[data-hint-pv="from"]'), function (el) {
      el.textContent = from || 'someone';
    });
    Array.prototype.forEach.call(modal.querySelectorAll('[data-hint-pv="to"]'), function (el) {
      el.textContent = to || 'you';
    });

    var noteEl = modal.querySelector('[data-hint-note]');
    if (noteEl) {
      noteEl.textContent = note ? '“' + note + '”' : '';
      noteEl.hidden = !note;
    }

    /* The text preview. The bubble is her words; the card beneath is what his
       phone builds from the product page, so it shows the real product image
       rather than a stand-in. */
    var smsText = modal.querySelector('[data-hint-smstext]');
    if (smsText) smsText.textContent = smsBody();

    var smsTitle = modal.querySelector('[data-hint-smstitle]');
    if (smsTitle) smsTitle.textContent = piece.title ? piece.title + ' | AVRA' : 'AVRA';

    var smsThumb = modal.querySelector('[data-hint-smsthumb]');
    if (smsThumb) {
      if (piece.image) {
        if (smsThumb.getAttribute('src') !== piece.image) smsThumb.src = piece.image;
        smsThumb.alt = piece.title;
        smsThumb.style.display = '';
      } else {
        smsThumb.style.display = 'none';
      }
    }
  }

  /* ------------------------------------------------------------------------
     Open / close
     ---------------------------------------------------------------------- */

  function open(button) {
    if (!modal) return;

    piece = {
      title: button.getAttribute('data-hint-title') || '',
      url: button.getAttribute('data-hint-url') || window.location.href,
      image: absolute(button.getAttribute('data-hint-image') || ''),
      price: button.getAttribute('data-hint-price') || '',
      type: button.getAttribute('data-hint-type') || '',
      material: button.getAttribute('data-hint-material') || ''
    };

    lastFocus = button;

    var name = modal.querySelector('[data-hint-name]');
    var meta = modal.querySelector('[data-hint-meta]');
    var thumb = modal.querySelector('[data-hint-thumb]');

    if (name) name.textContent = piece.title;

    if (meta) {
      var bits = [];
      if (piece.type) bits.push(piece.type);
      if (piece.material) bits.push('Material: ' + piece.material);
      meta.textContent = bits.join(' · ');
    }

    if (thumb) {
      if (piece.image) {
        thumb.src = piece.image;
        thumb.alt = piece.title;
        thumb.style.display = '';
      } else {
        thumb.style.display = 'none';
      }
    }

    setStatus('');

    /* Every open starts on email, with the text channel's leftovers cleared. */
    var copywrap = modal.querySelector('[data-hint-copywrap]');
    if (copywrap) copywrap.hidden = true;
    setChannel('email');

    modal.classList.remove('is-sent');
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    window.requestAnimationFrame(function () {
      modal.classList.add('is-visible');
      var first = modal.querySelector('input, textarea, button');
      if (first) first.focus();
    });
  }

  function close() {
    if (!modal) return;
    modal.classList.remove('is-visible');
    document.body.style.overflow = '';
    window.setTimeout(function () {
      modal.classList.remove('is-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }, 300);
  }

  function trap(event) {
    var focusable = modal.querySelectorAll('a[href], button:not([disabled]), input, textarea');
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ------------------------------------------------------------------------
     Validation
     ---------------------------------------------------------------------- */

  function validate(form) {
    var ok = true;

    Array.prototype.forEach.call(form.querySelectorAll('[data-required]'), function (input) {
      var field = input.closest('.hintfield');

      /* A field belonging to the other channel is not missing, it is not being
         asked for. Skip it rather than block the send on it. */
      if (field && field.hidden) {
        field.classList.remove('has-error');
        return;
      }

      var value = input.value.trim();
      var valid = value.length > 0;

      if (valid && input.type === 'email') {
        valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
      }

      /* Deliberately loose: AVRA ships beyond the US, and a number that is
         wrong simply opens her Messages app on the wrong thread, where she
         sees it before sending. */
      if (valid && input.type === 'tel') {
        valid = value.replace(/\D/g, '').length >= 7;
      }

      if (field) field.classList.toggle('has-error', !valid);

      if (!valid && ok) {
        input.focus();
        ok = false;
      }
    });

    return ok;
  }

  /* ------------------------------------------------------------------------
     The payload. Every channel below is handed these same names, and the
     email template uses them verbatim.
     ---------------------------------------------------------------------- */

  function buildPayload() {
    var note = val('hint_note');
    return {
      to_name: val('hint_to_name'),
      to_email: val('hint_to_email'),
      from_name: val('hint_from'),
      from_email: val('hint_from_email'),
      note: note,
      has_note: note ? 'yes' : 'no',
      piece_title: piece.title,
      piece_url: piece.url,
      piece_image: piece.image,
      piece_type: piece.type,
      piece_material: piece.material,
      piece_price: piece.price,
      shop_name: 'AVRA'
    };
  }

  /* ------------------------------------------------------------------------
     Channels
     ---------------------------------------------------------------------- */

  function sendKlaviyo(p) {
    var body = {
      data: {
        type: 'event',
        attributes: {
          properties: p,
          metric: { data: { type: 'metric', attributes: { name: CONFIG.klaviyo.metric } } },
          profile: {
            data: {
              type: 'profile',
              attributes: { email: p.to_email, first_name: p.to_name }
            }
          }
        }
      }
    };

    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(CONFIG.klaviyo.companyId), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: CONFIG.klaviyo.revision },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('Klaviyo responded ' + r.status);
      return true;
    });
  }

  function sendEmailJs(p) {
    return fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        service_id: CONFIG.emailjs.serviceId,
        template_id: CONFIG.emailjs.templateId,
        user_id: CONFIG.emailjs.publicKey,
        template_params: p
      })
    }).then(function (r) {
      if (!r.ok) throw new Error('EmailJS responded ' + r.status);
      return true;
    });
  }

  function sendWebhook(p) {
    return fetch(CONFIG.webhook.url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(p)
    }).then(function (r) {
      if (!r.ok) throw new Error('Webhook responded ' + r.status);
      return true;
    });
  }

  /* Fallback. Plain text only - see the CONFIG note. */
  function sendMailto(p) {
    var lines = [];
    lines.push(p.to_name ? 'Dear ' + p.to_name + ',' : 'Hi,');
    lines.push('');
    lines.push('We’ve been told ' + (p.from_name || 'someone') + ' has been dreaming about this piece… hint hint!');
    lines.push('');
    lines.push(p.piece_title);
    if (p.piece_type) lines.push(p.piece_type + ' · Material: ' + p.piece_material);
    lines.push(p.piece_url);
    if (p.note) {
      lines.push('');
      lines.push('A note from ' + (p.from_name || 'them') + ':');
      lines.push(p.note);
    }
    lines.push('');
    lines.push('AVRA');

    var subject = (p.to_name ? p.to_name + ', a' : 'A') + ' hint from AVRA';
    window.location.href = 'mailto:' + encodeURIComponent(p.to_email) +
      '?subject=' + encodeURIComponent(subject) +
      '&body=' + encodeURIComponent(lines.join('\n'));

    return Promise.resolve(true);
  }

  function deliver(p) {
    switch (mode()) {
      case 'klaviyo': return sendKlaviyo(p);
      case 'emailjs': return sendEmailJs(p);
      case 'webhook': return sendWebhook(p);
      default: return sendMailto(p);
    }
  }

  /* ------------------------------------------------------------------------
     Send
     ---------------------------------------------------------------------- */

  /* The text channel. Nothing is sent from AVRA and nothing here can find out
     whether her Messages app opened, so the message is always left on screen
     for her to copy instead of being reported as delivered. */
  function handOffToMessages(form) {
    var message = smsBody();
    var href = smsHref();

    var wrap = modal.querySelector('[data-hint-copywrap]');
    var text = modal.querySelector('[data-hint-copytext]');
    var done = modal.querySelector('[data-hint-donetext]');

    if (text) text.textContent = message;
    if (wrap) wrap.hidden = false;
    if (done) {
      done.textContent = 'We handed it to your Messages app with the note already written. ' +
        'If nothing opened, copy it below and send it yourself.';
    }

    document.dispatchEvent(new CustomEvent('avra:hint-texted', {
      detail: { message: message, piece: piece }
    }));

    setStatus('');
    modal.classList.add('is-sent');
    window.location.href = href;

    form.reset();
    paint();
  }

  function send(form) {
    if (sending) return;
    if (!validate(form)) return;

    if (channel === 'text') {
      handOffToMessages(form);
      return;
    }

    var payload = buildPayload();
    var button = form.querySelector('[data-hint-submit]');
    var how = mode();

    sending = true;
    if (button) { button.disabled = true; button.textContent = 'Sending…'; }
    setStatus(how === 'mailto' ? 'Opening your mail app…' : 'Sending…');

    deliver(payload).then(function () {
      document.dispatchEvent(new CustomEvent('avra:hint-sent', { detail: payload }));

      var done = modal.querySelector('[data-hint-donetext]');
      if (done) {
        done.textContent = how === 'mailto'
          ? 'We opened it in your mail app so the note stays in your words. Send it and the piece is their idea.'
          : 'It has landed in ' + (payload.to_name || 'their') + '’s inbox, with the piece and your note. The rest is their idea.';
      }

      setStatus('');
      modal.classList.add('is-sent');
      form.reset();
      paint();
    }).catch(function (error) {
      /* Never strand her. If the service is down, her own mail app still works. */
      setStatus('That did not go through. Opening your mail app instead…', true);
      window.setTimeout(function () { sendMailto(payload); }, 900);
      if (window.console && console.warn) console.warn('[AVRA hint]', error);
    }).then(function () {
      sending = false;
      if (button) { button.disabled = false; button.textContent = 'Send to'; }
    });
  }

  /* ------------------------------------------------------------------------
     Wiring
     ---------------------------------------------------------------------- */

  /* Clipboard access is refused in some in-app browsers, so selecting the text
     is the fallback — she can still copy it with her own keyboard or menu. */
  function copyMessage() {
    var el = modal && modal.querySelector('[data-hint-copytext]');
    var button = modal && modal.querySelector('[data-hint-copy]');
    if (!el) return;

    function selectInstead() {
      try {
        var range = document.createRange();
        range.selectNodeContents(el);
        var selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (error) {
        if (window.console && console.warn) console.warn('[AVRA hint]', error);
      }
    }

    function flash() {
      if (!button) return;
      button.textContent = 'Copied';
      window.setTimeout(function () { button.textContent = 'Copy message'; }, 2200);
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(el.textContent).then(flash, selectInstead);
    } else {
      selectInstead();
    }
  }

  function mount() {
    modal = document.querySelector('[data-hint-modal]');
    if (!modal || modal.dataset.hintBound) return;
    modal.dataset.hintBound = 'true';

    modal.addEventListener('click', function (event) {
      if (event.target.closest('[data-hint-close]') || event.target.hasAttribute('data-hint-scrim')) {
        close();
        return;
      }

      var toggle = event.target.closest('[data-hint-mode]');
      if (toggle) {
        setChannel(toggle.getAttribute('data-hint-mode'));
        return;
      }

      if (event.target.closest('[data-hint-copy]')) copyMessage();
    });

    modal.addEventListener('input', function (event) {
      if (event.target.hasAttribute('data-hint-bind')) paint();
    });

    var form = modal.querySelector('[data-hint-form]');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        send(form);
      });
    }
  }

  document.addEventListener('keydown', function (event) {
    if (!modal || !modal.classList.contains('is-open')) return;
    if (event.key === 'Escape') close();
    if (event.key === 'Tab') trap(event);
  });

  // Delegated so cards rendered later still work.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-hint-open]');
    if (!button) return;
    event.preventDefault();
    mount();
    open(button);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', mount);
})();
